/**
 * The app shell (decisions 12, 27, 28, 3; part 11 log, tables S, Y and D): the screen for the session's state, and
 * the workspace's layout: toolbar, chapter list beside the editor or over it, the editor, notices and dialogs. The
 * workspace stays in the page once it has been shown, so the open editor (and any typing it holds) is never torn down
 * by another screen covering it.
 */
import { createEffect, createMemo, on, onCleanup, untrack } from 'solid-js';
import type { Notice } from '../shell/workspace.ts';
import { commandFor } from '../shell/keys.ts';
import type { Command } from '../shell/keys.ts';
import { panes } from '../shell/layout.ts';
import { opensHelp } from '../help/first-visit.ts';
import { shownVersion } from '../help/guide.ts';
import { LONG_CHAPTER_WORDS } from '@cockatrice/core';
import { LocksUnsupportedError } from '@cockatrice/storage';
import type { AutosaveStatus } from '@cockatrice/editor';
import { ChapterList, focusAfterRemoval, shownBook, shownTitle } from './chapter-list.ts';
import { Announcer, cannotHoldFocus, focusLost, focusOn, saveNews, saveNewsText } from './announce.ts';
import type { SaveNews } from './announce.ts';
import { Dialogs } from './dialogs.ts';
import { el, view, when } from '../ui/dom.ts';
import { BundleStatus, bundleNews } from './bundle.ts';
import { EpubStatus, epubNews } from './epub.ts';
import { DocxStatus, docxNews } from './docx.ts';
import { BundleImportStatus, bundleImportNews } from './bundle-import.ts';
import { BackupStatus, backupNews } from './backup.ts';
import { ImportStatus, importNews } from './import.ts';
import { ReimportStatus, reimportNews } from './reimport.ts';
import { ImagesStatus, imagesNews } from './images.ts';
import { findNews, FindPanel, followFind, openFind } from './find.ts';
import { noteOnChosen, NotesPanel, SelectionBar } from './notes.ts';
import { goalNews, wordsNews } from './words.ts';
import { ExportStatus, exportNews } from './export.ts';
import { BrowserBanner, HomeScreenAsk, UpdateNotice, updateText } from './pwa.ts';
import { followPreview, PreviewPane, swipeHandlers, turnNews } from './preview.ts';
import { focusHome, Home } from './home.ts';
import { ReadingPage } from './reading.ts';
import { AccountBanner, AccountGate, gateStep, ReadOnlyNote } from './account.ts';
import { ManageBanners } from './account-banners.ts';
import { ChapterRail, ManuscriptHead, RailFoot, ViewTabs, WriterBar } from './writer-bar.ts';
import { leaveSettings, SettingsPage } from './settings-view.ts';
import { SharingPage } from './sharing.ts';
import Banner from '../ui/Banner.ts';
import Button from '../ui/Button.ts';
import EmptyState from '../ui/EmptyState.ts';
import Spinner from '../ui/Spinner.ts';
import { component } from '../ui/solid.ts';
import type { Child, On } from '../ui/dom.ts';
import type { Shell } from './shell.ts';

/** The text of a notice (C-rows' outcomes). */
export function noticeText(shell: Shell, notice: Notice): string {
  const { t } = shell;
  switch (notice.kind) {
    case 'save-failed':
      return t('notice.saveFailed');
    case 'autosave-failed':
      return t('notice.autosaveFailed');
    case 'write-failed':
      return t('notice.writeFailed');
    case 'copy-kept':
      return t('notice.copyKept');
    case 'unreadable':
      return t('notice.unreadable');
    case 'read-failed':
      return t('notice.readFailed');
    case 'title-refused':
      return t('notice.titleRefused');
    case 'notes-failed':
      return t('notice.notesFailed');
    case 'notes-joined':
      return t('notice.notesJoined');
    case 'notes-lost':
      return t('notice.notesLost');
    case 'note-gone':
      return t('notice.noteGone');
    case 'note-failed':
      return t('notice.noteFailed');
    case 'unsaved-waiting':
      return t('notice.unsavedWaiting');
    case 'split-refused':
      switch (notice.problem) {
        case 'not-a-point':
          return t('notice.splitNotAPoint');
        case 'empty-piece':
          return t('notice.splitEmptyPiece');
        case 'notes':
          return t('notice.splitNotes');
        case 'reads-back':
          return t('notice.splitReadsBack');
        case 'too-long':
          return t('notice.splitTooLong');
      }
  }
}

/** The open chapter's title as shown; empty with none open, or one the listing doesn't hold (a listing not read again). */
export function openTitle(shell: Shell): string {
  const entry = shell.workspace.listing()?.chapters.find((each) => each.id === shell.workspace.chapterId());
  return entry === undefined ? '' : shownTitle(shell, entry);
}

/** C5: opens the split dialog for the open chapter as it is now; nothing with no chapter open. */
export function offerSplit(shell: Shell): void {
  const offer = shell.workspace.splitOffer();
  if (offer === null) return;
  const title = openTitle(shell);
  shell.ui.setDialog({ kind: 'split', offer, title: title === '' ? shell.t('chapter.untitled') : title });
}

/**
 * A16, A24 (part 11c): closes the chapter list, the focus going back to the toolbar's toggle when it was in the list (the
 * drawer holds the focus while open, so closing it always does).
 */
export function closeList(shell: Shell): void {
  const inside = (document.activeElement?.closest('.list-pane') ?? null) !== null;
  shell.ui.setListOpen(false);
  if (inside || focusLost()) focusOn(shell, '.toggle-list');
}

/** Whether `target` is an element that took the focus now (one in something hidden, a closed menu, never does). */
function focused(target: Element | null): boolean {
  if (!(target instanceof HTMLElement) || target.closest('[hidden]') !== null) return false;
  target.focus();
  return document.activeElement === target;
}

/** Runs a shortcut's command (K6). */
export function runCommand(shell: Shell, command: Command): void {
  const { workspace, ui } = shell;
  switch (command) {
    case 'focusMode':
      // A24: the toolbar or the list that had the focus may go with focus mode; the focus guard (A24b) takes it on.
      void workspace.updateSettings({ focusMode: !workspace.settings().focusMode });
      return;
    case 'chapterList':
      // In focus mode the list is hidden whatever it is set to, so the key leaves focus mode with the list open.
      if (workspace.settings().focusMode) {
        void workspace.updateSettings({ focusMode: false });
        ui.setListOpen(true);
      } else if (ui.listOpen()) closeList(shell);
      else ui.setListOpen(true);
      return;
    case 'previousChapter':
    case 'nextChapter':
      void workspace.stepChapter(command === 'nextChapter' ? 1 : -1).then(shell.focusEditor);
      return;
    case 'newChapter':
      void workspace.createChapter().then(shell.focusEditor);
      return;
    case 'settings':
      shell.go('settings');
      return;
    case 'help':
      ui.setDialog({ kind: 'help', first: false });
      return;
    case 'find':
      // Part 19a, F1: the panel shows with the toolbar, so focus mode is left first.
      openFind(shell);
      return;
    case 'note':
      // Part 19e, Y′: the selection bar's Private note, by key.
      noteOnChosen(shell);
      return;
  }
}

/**
 * The page's keys (K6, Y4; part 11b, K10, K12): a shortcut runs its command unless a dialog is open or the editor or a
 * control already took the key; Escape leaves focus mode or closes the drawer.
 */
export function onKey(shell: Shell, event: KeyboardEvent): void {
  const { workspace, ui } = shell;
  const command = commandFor(workspace.keys(), event, shell.platform);
  // K12 (part 11b, deep-fix r1): help's key (F1 by default) is the browser's own help elsewhere, which would open a tab
  // over the app, so it never reaches the browser, whatever the app is showing; it runs only where the help can open.
  if (command === 'help') event.preventDefault();
  // H2: nothing runs under the Home Screen screen.
  // Part 36a: nor under the account's gate.
  if (ui.dialog() !== null || ui.closeUpOpen() || shell.workspace.screen() !== 'workspace' || shell.homeScreen.state() === 'ask' || gateStep(shell) !== null) return;
  // Part 11e, SL2: Escape leaves the settings, unless a control there took it (a key capture, a menu).
  if (event.key === 'Escape' && !event.defaultPrevented && ui.route() === 'settings') {
    event.preventDefault();
    leaveSettings(shell);
    return;
  }
  if (event.key === 'Escape' && !event.defaultPrevented && ui.route() === 'write') {
    if (workspace.settings().focusMode) void workspace.updateSettings({ focusMode: false });
    else if (shell.mode() === 'narrow' && ui.listOpen()) closeList(shell);
    else return;
    event.preventDefault();
    return;
  }
  // K10: the help only reads, so it opens while the workspace is paused too, as the toolbar's Help does. Part 11d, R7:
  // away from the writer only the help and the settings run; the rest act on the open text.
  if (command === null || (command !== 'help' && !workspace.editable())) return;
  if (ui.route() !== 'write' && command !== 'help' && command !== 'settings') return;
  event.preventDefault();
  // Part 19e, deep-fix r3: on a narrow screen the Notes sheet is modal over what the commands act on (the bar, the text),
  // so it closes first; Private note is its own.
  if (shell.mode() === 'narrow' && ui.notesOpen() && command !== 'note') {
    ui.setNotesOpen(false);
    void workspace.flushNotes();
  }
  runCommand(shell, command);
}

/** The open book's name, the page's heading: its title, or what it is when it has none to show. */
export function bookTitle(shell: Shell): string {
  const book = shell.workspace.book();
  return book === null ? shell.t('app.name') : shownBook(shell, book);
}

/** What the toolbar says about the open chapter's typing (decision 3: the unsaved indicator); nothing with none open. */
export function statusText(shell: Shell, status: AutosaveStatus | null): string {
  switch (status) {
    case null:
      return '';
    case 'saved':
    case 'closed':
      return shell.t('status.saved');
    case 'unsaved':
      return shell.t('status.unsaved');
    case 'saving':
      return shell.t('status.saving');
    case 'failed':
      return shell.t('status.failed');
  }
}

/** Fix-next 43: the same state in the phone bar's short words, beside the book's title. */
export function shortStatusText(shell: Shell, status: AutosaveStatus | null): string {
  switch (status) {
    case null:
      return '';
    case 'saved':
    case 'closed':
      return shell.t('status.savedShort');
    case 'unsaved':
      return shell.t('status.unsavedShort');
    case 'saving':
      return shell.t('status.savingShort');
    case 'failed':
      return shell.t('status.failedShort');
  }
}

/**
 * Y3, part 11c (A3, A16): the chapter list on a narrow screen, a modal dialog over the workspace. The browser keeps the
 * focus in it and everything behind it inert while it is open, and gives the focus to its first control, its close
 * button. A phone has no Escape (*deep-fix r5*): its button, or a tap beside it (on the dialog's backdrop, which the
 * dialog itself receives), closes it; Escape does where there is one. A pointer's release, not a click: Safari sends a
 * tap on a plain element as no click at all.
 */
function Drawer(shell: Shell, announcers: () => Child): Child {
  return el(
    'dialog',
    {
      class: 'list-pane drawer',
      'aria-label': shell.t('nav.label'),
      ref: (node: HTMLDialogElement) => {
        // Made before it is in the page; shown modal once it is.
        queueMicrotask(() => {
          if (node.isConnected && !node.open) node.showModal();
        });
        onCleanup(() => {
          node.close();
        });
      },
      onPointerUp: (event: On<PointerEvent, HTMLDialogElement>) => {
        if (event.target !== event.currentTarget) return;
        const box = event.currentTarget.getBoundingClientRect();
        const inside = event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
        if (!inside) closeList(shell);
      },
      onCancel: (event: Event) => {
        event.preventDefault();
        closeList(shell);
      },
    },
    component(Button, {
      variant: 'ghost',
      iconStart: 'panel',
      class: 'drawer-close',
      ref: (button: HTMLButtonElement) => {
        queueMicrotask(() => {
          button.focus();
        });
      },
      onClick: () => {
        closeList(shell);
      },
      children: shell.t('toolbar.hideChapters'),
    }),
    NoticeView(shell),
    // Part 11f, WT14: the book's words at the drawer's foot, as the rail has them.
    ChapterList(shell, RailFoot(shell)),
    announcers(),
  );
}

/**
 * The notice on screen, with its Dismiss (C-rows): over the text, and inside the open drawer too, which covers the text
 * (part 11c, deep-fix r2). It is said by the workspace's notice announcer, an alert, not by itself.
 */
function NoticeView(shell: Shell): Child {
  const { t, workspace } = shell;
  return () => {
    const notice = workspace.notice();
    return notice === null
      ? null
      : untrack(() =>
          component(Banner, {
            tone: 'danger',
            class: 'notice',
            children: el('p', {}, noticeText(shell, notice)),
            actions: component(Button, {
              size: 'sm',
              onClick: () => {
                workspace.dismissNotice();
                // A24: the notice goes with its button.
                focusAfterRemoval(shell);
              },
              children: t('notice.dismiss'),
            }),
          }),
        );
  };
}

/** The workspace (S1): list, editor, notices and banners in the adaptive layout (table Y). */
function WorkspaceView(shell: Shell): Child {
  const { t, workspace, ui } = shell;
  const writing = createMemo(() => ui.route() === 'write');
  // Part 19e, D′10 (deep-fix r2): the Notes sheet on a narrow screen is modal: everything behind it is inert, so the focus
  // stays in it.
  const sheet = (): boolean => shown().notes && shell.mode() === 'narrow';
  const shown = createMemo(() =>
    panes({ writing: writing(), mode: shell.mode(), focusMode: workspace.settings().focusMode, listOpen: ui.listOpen(), layout: workspace.settings().writerLayout, narrowPane: ui.narrowPane(), notesOpen: ui.notesOpen() }),
  );
  // Part 11d, R5, R6: a page shown takes the focus: the writer puts it in the text, home on its heading (its books when
  // no book is open yet), the settings on theirs; coming back from the settings, Settings takes it where it shows (part
  // 11e, SL2). The first page needs none: the page's own load leaves it at the start.
  createEffect(
    on(
      ui.route,
      (route, previous) => {
        // SL2 (D1's rule for the dialog it was): a key capture ends with the page.
        if (previous === 'settings') ui.setCapturing(null);
        queueMicrotask(() => {
          // Settings, or in the writer the More menu it sits in (part 11f, WT11).
          if (previous === 'settings' && (focused(document.querySelector('.open-settings')) || focused(document.querySelector('.more-menu')))) return;
          if (route === 'write') shell.focusEditor();
          else if (route === 'home') focusHome();
          // Part 38c: Shared with me on its heading (scrolled to); a read view on its heading (the chapter's title, or
          // what it says while there is no text: the view always has one).
          else if (route === 'shared') {
            const heading = document.getElementById('shared-title');
            heading?.scrollIntoView({ block: 'start' });
            focused(heading);
          } else if (route === 'reading') focused(document.getElementById('read-title'));
          else {
            // Part 36a: opened at a section, its heading (scrolled to); otherwise the page's.
            const at = ui.settingsAt();
            ui.setSettingsAt(null);
            const heading = document.getElementById(at === null ? 'settings-title' : `${at}-title`);
            if (at !== null) heading?.scrollIntoView({ block: 'start' });
            focused(heading);
          }
        });
      },
      { defer: true },
    ),
  );
  followPreview(shell, shown);
  // Y1–Y3 (*deep-fix r6*): the list follows the layout across its line: closed when the screen turns narrow (a rotated
  // tablet, a snapped window), so no drawer lands over the text; open again as the sidebar when it turns wide.
  createEffect(
    on(
      shell.mode,
      () => {
        // Part 11f, WT1: either way the list closes: the drawer goes, and the rail shows the chapters' numbers.
        ui.setListOpen(false);
        // Y10: a screen turning narrow shows the text first.
        ui.setNarrowPane('text');
      },
      { defer: true },
    ),
  );
  // Part 19a, F8: the panel put away with the toolbar (focus mode) closes, its highlights with it.
  followFind(shell, () => shown().toolbar);
  // Part 19a, F10, F11: another chapter or book opened, or the book read again: searched again while the panel is open.
  createEffect(on(workspace.chapterId, () => { shell.finder.chapterOpened(); }, { defer: true }));
  // Deep-fix r1: so do the open book's live chapters changing (trash, restore, delete forever, a new chapter, a split,
  // a reorder), which the results' chapters and order stand for.
  const liveChapters = createMemo(() => (workspace.listing()?.chapters ?? []).map((entry) => entry.id).join(' '));
  createEffect(on([() => workspace.book()?.id, workspace.bookReading, liveChapters], () => { shell.finder.bookChanged(); }, { defer: true }));
  // Part 19b, D1, D3: today's words follow the open editor, from its text as it opens; B7: the dialog counts again.
  createEffect(on(workspace.editor, () => { shell.counter.editorChanged(); }));
  // Part 38d, table PL: a book's reading links pull their comments in as it opens (and once this device can share).
  createEffect(
    on(workspace.openBookId, (bookId) => {
      if (bookId !== null) void shell.sharing.pullBook(bookId);
    }),
  );
  createEffect(on([() => workspace.book()?.id, workspace.bookReading, liveChapters, workspace.chapterId], () => { shell.counter.bookChanged(); }, { defer: true }));
  // Part 11d, H5: the open book counted whole for the home page, as it opens and whenever its chapters change.
  createEffect(on([() => workspace.book()?.id, workspace.bookReading, liveChapters, workspace.chapterId], () => { shell.counter.shelfChanged(); }));
  const finding = findNews(shell);
  // A13: the save state's news, from its news before (principle 3).
  const news = createMemo<SaveNews>((before) => saveNews(before, workspace.status()), 'none');
  // A13, deep-fix r1: the workspace's news, in the banner and again in each modal over it (the drawer, a dialog): a
  // modal hides everything outside it from assistive technology, live regions included. Only the topmost copy is
  // exposed, and a copy made while the news stands is silent, so each piece of news is heard once, wherever it lands.
  const announcers = (): Child => [
    // Deep-fix r2: the notice too, assertive as the alert it was (table N); cleared when dismissed, so the next is said.
    Announcer('notice', () => {
      const notice = workspace.notice();
      return notice === null ? '' : noticeText(shell, notice);
    }, 'alert'),
    Announcer('save', () => saveNewsText(shell, news())),
    // A13: the long-chapter banner is said once, as the chapter grows past the limit.
    Announcer('long-chapter', () => (workspace.longChapter() ? t('long.body', { words: LONG_CHAPTER_WORDS }) : '')),
    Announcer('export', () => exportNews(shell, shell.exporter.state())),
    // Part 20, Y2: the EPUB export's phases.
    Announcer('epub', () => epubNews(shell, shell.epub.state())),
    // Part 22, Y3: the DOCX export's phases.
    Announcer('docx', () => docxNews(shell, shell.docx.state())),
    Announcer('bundle', () => bundleNews(shell, shell.bundler.state())),
    // Part 24e, Y′3: the backups' notice and reminder.
    Announcer('backup', () => backupNews(shell)),
    // Part 18: the import's phases and outcome.
    Announcer('import', () => importNews(shell, shell.importer.state())),
    // Part 23b, Y: the re-import's phases and outcome.
    Announcer('reimport', () => reimportNews(shell, shell.reimporter.state())),
    // Part 24c, Y2: the bundle import's phases and outcome.
    Announcer('bundle-import', () => bundleImportNews(shell, shell.restorer.state())),
    // Part 19a, Y: a move's place, a replace's news, a search's results once settled.
    Announcer('find', finding),
    // Part 19b, B2, G6: the book's count once known, and the daily goal reached.
    Announcer('words', () => wordsNews(shell)),
    Announcer('goal', () => goalNews(shell)),
    // Part 16b, Y3: each image's progress, and what became of the files.
    Announcer('images', () => imagesNews(shell, shell.images.state())),
    Announcer('update', () => updateText(shell, shell.pwa.update())),
    // Part 15a, BA6: a turn of the book view says where it went, once.
    Announcer('turn', () => turnNews(shell, shell.preview.state())),
  ];
  // Part 18, V5: a book read opens its review.
  createEffect(
    on(shell.importer.state, (state) => {
      if (state.kind === 'review') ui.setDialog({ kind: 'import', review: state.review });
    }),
  );
  // Part 23b, V′5: a file merged opens its review.
  createEffect(
    on(shell.reimporter.state, (state) => {
      if (state.kind === 'review') ui.setDialog({ kind: 'reimport', fileName: state.fileName, review: state.review, pictures: state.pictures });
    }),
  );
  // Y3 (*deep-fix r5*): a chapter opening (a new one, kept text made a chapter, a shortcut) closes the drawer over it.
  createEffect(
    on(
      workspace.chapterId,
      (chapterId) => {
        if (chapterId !== null && shell.mode() === 'narrow') ui.setListOpen(false);
      },
      { defer: true },
    ),
  );
  // The statuses of jobs under way (exports, imports, backups, images) and the app's banners: under the writer's toolbar,
  // or under the home page's header, never over the text.
  const statuses = (): Child =>
    el(
      'div',
      { class: 'statuses' },
      // Part 36a: why the books are read-only, or that this device was logged out.
      AccountBanner(shell),
      // Part 36c, C11: what waits on the account (a reset, the deletion, a change), a new device, the terms.
      ManageBanners(shell),
      ExportStatus(shell),
      EpubStatus(shell),
      DocxStatus(shell),
      BundleStatus(shell),
      BackupStatus(shell),
      ImportStatus(shell),
      ReimportStatus(shell),
      BundleImportStatus(shell),
      ImagesStatus(shell),
      UpdateNotice(shell, false),
      BrowserBanner(shell),
    );
  return [
    // Part 11d, R4: the home page, or the settings (part 11e), in the page's flow, over the writer, which stays laid out
    // under them (decision 14: Continue writing to typing), fixed to the window, unseen and inert, with its typing; the
    // job statuses show on whichever page is up.
    // Each made untracked (as a component is), so a signal its making reads never makes it again.
    when(() => ui.route() === 'home' || ui.route() === 'shared', () => untrack(() => Home(shell, statuses))),
    // Part 38c: a shared book's read view, a page of its own as the home page is.
    when(() => ui.route() === 'reading', () => untrack(() => ReadingPage(shell, statuses))),
    when(() => ui.route() === 'settings', () => untrack(() => SettingsPage(shell, statuses))),
    // Part 38d: the Sharing page, as the home page is.
    when(() => ui.route() === 'sharing', () => untrack(() => SharingPage(shell, statuses))),
    el(
      'div',
      {
        class: () => `workspace ${shell.mode()} layout-${workspace.settings().writerLayout}${writing() ? '' : ' covered'}${workspace.settings().focusMode ? ' focus' : ''}${shown().drawer ? ' drawer-open' : ''}`,
        'data-font': () => workspace.settings().editorFont,
        style: () => ({ '--editor-size': `${workspace.settings().fontSize}px` }),
      },
      // The workspace's news, said wherever the page is (part 11c, A13), outside anything a page hides.
      el('div', { class: 'announcers' }, announcers()),
      // The banner: a way past it to the text, the book's name as the page's heading, and the toolbar (Y5).
      el(
        'header',
        { class: 'banner', inert: () => !writing() || sheet() },
        el('a', { class: 'skip', href: '#chapter-text' }, t('skip.editor')),
        el('h1', { class: 'visually-hidden' }, () => bookTitle(shell)),
        // Part 19a, deep-fix r5: on a narrow screen the open find panel takes the toolbar's place, so the text keeps its
        // room above the phone's keyboard; Close brings the toolbar back. Focus mode hides the save state the same way
        // (decision 3's failures are the notice's to say).
        when(() => shown().toolbar && !(shell.mode() === 'narrow' && shell.finder.open()), () => WriterBar(shell, () => statusText(shell, workspace.status()), () => shortStatusText(shell, workspace.status()))),
        when(() => shown().toolbar && shell.finder.open(), () => FindPanel(shell)),
        when(writing, statuses),
      ),
      // Part 11f, WT1, WT14: the chapter rail, numbers or the whole list, on a wide screen out of focus mode.
      when(
        () => shell.mode() === 'wide' && !workspace.settings().focusMode,
        () => el('div', { class: 'wr-rail-host', inert: () => !writing() }, ChapterRail(shell)),
      ),
      // Deep-fix r1: only while the workspace is the screen. A modal left open under another screen (another tab took over,
      // the store failed or was upgraded, the Home Screen screen) would keep that screen's buttons from taking a press.
      when(
        () => shown().drawer && workspace.screen() === 'workspace' && shell.homeScreen.state() !== 'ask',
        () => Drawer(shell, announcers),
      ),
      el(
        'main',
        {
          class: 'editor-pane',
          id: 'chapter-text',
          tabIndex: -1,
          // Y9: a narrow screen showing the preview keeps the text in the page, hidden, with its typing; so does home (R4).
          hidden: () => !shown().editor,
          inert: () => !shown().editor || !writing() || sheet(),
          ...swipeHandlers(shell, 'text'),
          // The text is only as tall as its lines: a press on the pane's empty space (beside, above or below the text)
          // would otherwise focus the pane and leave the writer with no caret. Presses inside the text, a notice or a
          // button reach their own target.
          onMouseDown: (event: On<MouseEvent, HTMLElement>) => {
            if (event.button !== 0 || (event.target !== event.currentTarget && event.target !== shell.editorHost)) return;
            if (shell.placeCaret(event.clientX, event.clientY, event.shiftKey)) event.preventDefault();
          },
        },
        NoticeView(shell),
        when(
          () => workspace.longChapter() && !workspace.settings().focusMode,
          () =>
            component(Banner, {
              tone: 'warn',
              class: 'long-chapter',
              children: el('p', {}, t('long.body', { words: LONG_CHAPTER_WORDS })),
              actions: component(Button, {
                size: 'sm',
                get disabled() {
                  return !workspace.writable();
                },
                onClick: () => {
                  offerSplit(shell);
                },
                children: t('long.split'),
              }),
            }),
        ),
        when(
          () => workspace.chapterId() === null,
          () => el('p', { class: 'no-chapter' }, t('editor.none')),
        ),
        // Part 11f: the open chapter's number and title over its text, and the formatting bar.
        when(
          () => workspace.chapterId() !== null && !workspace.settings().focusMode,
          () => ManuscriptHead(shell),
        ),
        shell.editorHost,
        // Part 36a, RO: the text is read-only, and why (the design's paused note, after the text).
        ReadOnlyNote(shell),
        // Part 19e, Y′: Private note while text is chosen.
        SelectionBar(shell),
        when(
          () => workspace.settings().focusMode,
          () =>
            component(Button, {
              size: 'sm',
              class: 'leave-focus',
              onClick: () => {
                void workspace.updateSettings({ focusMode: false });
                // A24: the button goes with focus mode; the text keeps the caret.
                shell.focusEditor();
              },
              children: t('toolbar.leaveFocus'),
            }),
        ),
      ),
      when(
        () => shown().preview,
        () => PreviewPane(shell, () => shown().drawer || sheet(), () => !shown().editor),
      ),
      // Part 19e, table D′: the Notes panel, beside the text or as a sheet.
      when(
        () => shown().notes,
        () => untrack(() => NotesPanel(shell)),
      ),
      // Part 11f, WL4: on a narrow screen, the switch between the text and the pages.
      when(
        // Deep-fix r2: not under the Notes sheet, which is modal.
        () => shell.mode() === 'narrow' && shown().toolbar && !sheet(),
        () => ViewTabs(shell),
      ),
      // Part 11d, R4: the preview's column, empty, while the writer is covered.
      when(
        () => shown().previewSpace,
        () => el('div', { class: 'preview-pane', 'aria-hidden': 'true' }),
      ),
      Dialogs(shell, announcers),
    ),
  ];
}

/**
 * Part 11c, deep-fix r4: puts the focus on the screen showing (not the workspace): its first button that can take it, or
 * its heading where it has none (loading, closed). Deep-fix r6: never on a button that can lose the writer's work: the
 * failed screen kept over the workspace (S11) is there so the text can be copied first, and its Try again reloads the
 * page, so a reflex Space or Enter mustn't reach it; its heading takes the focus.
 */
export function focusScreen(): void {
  const shown = document.querySelector('.app > .screen');
  if (shown === null) return;
  for (const selector of shown.classList.contains('failed-kept') ? ['h1, h2'] : ['button', 'h1, h2']) {
    for (const each of shown.querySelectorAll(selector)) {
      if (!(each instanceof HTMLElement) || Reflect.get(each, 'disabled') === true) continue;
      each.focus();
      if (document.activeElement === each) return;
    }
  }
}

/**
 * S4, S5: another tab edits, or this one couldn't take the lock; "use here" (L1). PLAN.md §3, step 8: the session screens
 * are EmptyStates (icon, h1, words, one button), the page's heading named and focusable as before.
 */
function Elsewhere(shell: Shell): Child {
  const { t, workspace } = shell;
  return el(
    'main',
    { class: 'screen', 'aria-labelledby': 'screen-heading' },
    component(EmptyState, {
      icon: 'devices',
      title: t('elsewhere.title'),
      headingLevel: 1,
      headingId: 'screen-heading',
      headingFocusable: true,
      children: [
        el('p', { id: 'screen-body' }, t('elsewhere.body')),
        when(
          () => workspace.failure() !== undefined,
          () => component(Banner, { tone: 'danger', class: 'failure', children: el('p', { id: 'screen-failure' }, t('elsewhere.failed')) }),
        ),
      ],
      // Deep-fix r5: the screen's message is read with the button that takes the focus as it shows (r4), so a takeover
      // that failed says why, not only "Use here" again.
      actions: component(Button, {
        variant: 'primary',
        autofocus: true,
        'aria-describedby': () => (workspace.failure() === undefined ? 'screen-body' : 'screen-failure screen-body'),
        onClick: () => void workspace.useHere(),
        children: t('elsewhere.useHere'),
      }),
    }),
    UpdateNotice(shell),
  );
}

/** S6, S10: a later build upgraded the store; reload, through the waiting build when there is one (part 40a, U11). */
function Outdated(shell: Shell): Child {
  const { t } = shell;
  return el(
    'main',
    { class: 'screen', 'aria-labelledby': 'screen-heading' },
    component(EmptyState, {
      icon: 'refresh',
      title: t('outdated.title'),
      headingLevel: 1,
      headingId: 'screen-heading',
      headingFocusable: true,
      children: el('p', { id: 'screen-body' }, t('outdated.body')),
      actions: component(Button, {
        variant: 'primary',
        autofocus: true,
        'aria-describedby': 'screen-body',
        onClick: () => {
          shell.pwa.restart();
        },
        children: t('outdated.reload'),
      }),
    }),
  );
}

/** S11: the store can't open, or the browser has no Web Locks (part 06, E16). */
function Failed(props: { readonly shell: Shell; readonly kept: boolean }): Child {
  const { shell, kept } = props;
  const { t, workspace } = shell;
  const locks = (): boolean => {
    const failure = workspace.failure();
    return failure instanceof LocksUnsupportedError;
  };
  // S11 after the workspace was shown: it stays on screen below, read-only, so the writer can copy the open chapter's
  // text before trying again; a reload would take it with the page (*deep-fix r2*). Kept, it is an inline state over
  // the workspace, its button secondary (a reflex press would reload the page).
  return el(
    kept ? 'section' : 'main',
    { class: kept ? 'screen failed-kept' : 'screen', role: kept ? 'alert' : undefined, 'aria-labelledby': 'screen-heading' },
    component(EmptyState, {
      icon: 'warn',
      size: kept ? 'inline' : 'page',
      title: t('failed.title'),
      headingLevel: kept ? 2 : 1,
      headingId: 'screen-heading',
      headingFocusable: true,
      children: [el('p', { id: 'screen-body' }, () => (locks() ? t('failed.locks') : t('failed.store'))), kept ? el('p', { id: 'screen-kept' }, t('failed.kept')) : null],
      actions: component(Button, {
        variant: kept ? 'secondary' : 'primary',
        autofocus: !kept,
        'aria-describedby': kept ? 'screen-body screen-kept' : 'screen-body',
        onClick: () => {
          shell.reload();
        },
        children: t('failed.retry'),
      }),
    }),
    // Part 40a: a new build may be what fixes it.
    UpdateNotice(shell),
  );
}

/**
 * The app: the screen for the workspace's state (table S), with the page's keys (table K); on iOS outside the Home Screen
 * app, the Home Screen screen first (part 40a, H2); on a device's first visit, the help (part 11b, table V).
 */
export function App(shell: Shell): Child {
  const screen = createMemo(() => shell.workspace.screen());
  const asking = (): boolean => shell.homeScreen.state() === 'ask';
  // Part 36a, table AC: the account's gate has the page over the workspace while it shows (its card changes inside it).
  // Deep-fix r3, table VL; r4, GS: a card the writer or a link opened needs no book and no lock, so it shows over whatever
  // screen is up (another tab holding the book, the store failing, the Home Screen screen), or a link, already out of the
  // address, is lost; the first start's own card waits for the workspace.
  const linkOpen = createMemo(() => shell.ui.accountStep() !== null);
  const gate = createMemo(() => linkOpen() || (screen() === 'workspace' && gateStep(shell) !== null));
  /** Whether a screen other than the workspace has the page (the workspace hidden under it, or under the failed one). */
  const covering = (): boolean => asking() || screen() !== 'workspace' || gate();
  const seen = createMemo<boolean>((before) => before || screen() === 'workspace', false);
  const listener = (event: KeyboardEvent): void => {
    onKey(shell, event);
  };
  window.addEventListener('keydown', listener);
  // Tenet 3: while saving fails, the browser asks before the page goes, which would take the typing with it (*deep-fix r3*).
  const beforeUnload = (event: BeforeUnloadEvent): void => {
    if (shell.workspace.leaving()) event.preventDefault();
  };
  window.addEventListener('beforeunload', beforeUnload);
  // Part 11c, deep-fix r2 (table A24b): whatever takes the focused control away (its own press, a job ending, a pause, a
  // layout change, focus mode set in the settings, a dialog closing onto an opener that went), the focus doesn't stay
  // on the page: it goes where removing a control leaves it. A press that knows a better next step moves it first, and
  // then this finds the focus held. One check of the focus per change of the page, nothing more on the keystroke path.
  let focused: Element | null = null;
  const tracked = (event: FocusEvent): void => {
    focused = event.target instanceof Element ? event.target : null;
  };
  document.addEventListener('focusin', tracked);
  const keeper = new MutationObserver(() => {
    // The focus fell to the page, or stayed on an element turned off (browsers differ on which).
    if (focused === null || !cannotHoldFocus(focused) || (!focusLost() && document.activeElement !== focused)) return;
    focused = null;
    // Deep-fix r4: another screen covering the workspace takes the focus itself.
    if (covering()) focusScreen();
    else focusAfterRemoval(shell);
  });
  // Deep-fix r4 (the user's choice, 2026-10-07): every change of screen (table S, the Home Screen screen) puts the focus
  // on that screen's first action, its heading read with it as the screen's name; `autofocus` alone acts once a page.
  createEffect(
    on([screen, asking], () => {
      if (covering()) queueMicrotask(focusScreen);
    }),
  );
  keeper.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'hidden', 'inert', 'open'] });
  // D1: what the workspace has open closes with it, so a modal dialog under another screen never blocks that screen's
  // buttons (*deep-fix r1*).
  // Part 36c, LK: so does a card over it (a mailed link opened while a sheet was open), which the modal would cover.
  createEffect(() => {
    if (screen() === 'workspace' && !gate()) return;
    shell.ui.setDialog(null);
    shell.ui.setCapturing(null);
    shell.ui.setRenaming(null);
  });
  // Deep-fix r4, table GS: another tab logged this device in: a card asking for an account closes (the second step and a
  // link's card stay: what they did is still on them).
  createEffect(
    on(
      () => shell.account.status(),
      (status) => {
        const step = untrack(() => shell.ui.accountStep());
        if (status.kind === 'account' && !status.signedOut && (step === 'welcome' || step === 'create' || step === 'login' || step === 'check-email')) shell.ui.setAccountStep(null);
      },
    ),
  );
  // Part 36c, C11: the account's lists read when the account logs in here and whenever the page is shown again (at most
  // once a pause), so what waits on the account shows on every device.
  createEffect(
    on([() => {
      const status = shell.account.status();
      return status.kind === 'account' && !status.signedOut ? status.id : null;
    }, shell.pageVisible], ([id, visible]) => {
      if (id !== null && visible) void shell.manager.load();
    }),
  );
  // Part 36a, table RO: the open editor follows the account's permit (it saves and pauses when writing stops).
  createEffect(
    on(
      () => shell.account.permit(),
      () => {
        shell.workspace.permitChanged();
      },
      { defer: true },
    ),
  );
  // Part 11d (decision 77): the chosen colours are the whole page's, every screen's, not only the writer's.
  createEffect(() => {
    document.documentElement.dataset['theme'] = shell.workspace.settings().editorTheme;
  });
  // V1–V9: the help opens by itself once the workspace can store that it did; showing it records the version (V12).
  createEffect(() => {
    const state = {
      screen: screen(),
      editable: shell.workspace.editable(),
      // Part 36a: the help waits for the account's gate as it waits for the Home Screen screen; BK2: and opens on no
      // store without a book (the empty one shown after a logout), whose settings are nobody's.
      asking: asking() || gate() || shell.workspace.book() === null,
      dialog: shell.ui.dialog() !== null,
      helpSeen: shell.workspace.settings().helpSeen,
      version: shownVersion(shell.t),
    };
    if (opensHelp(state)) shell.ui.setDialog({ kind: 'help', first: true });
  });
  onCleanup(() => {
    window.removeEventListener('keydown', listener);
    window.removeEventListener('beforeunload', beforeUnload);
    document.removeEventListener('focusin', tracked);
    keeper.disconnect();
  });
  return el(
    'div',
    { class: 'app', lang: shell.locale },
    () => {
      if (linkOpen()) return view(AccountGate, shell);
      if (asking()) return view(HomeScreenAsk, shell);
      switch (screen()) {
        // Part 11c: every screen has the page's heading; these two say only what the app is doing.
        case 'loading':
          return el('main', { class: 'screen screen-wait' }, el('h1', { class: 'visually-hidden', tabIndex: -1 }, shell.t('app.name')), component(Spinner, { size: 'lg', label: shell.t('app.loading'), showLabel: true }));
        case 'workspace':
          return gate() ? view(AccountGate, shell) : null;
        case 'elsewhere':
          return view(Elsewhere, shell);
        case 'outdated':
          return view(Outdated, shell);
        case 'failed':
          return view(Failed, { shell, kept: seen() });
        case 'closed':
          return el('main', { class: 'screen screen-wait' }, el('h1', { class: 'visually-hidden', tabIndex: -1 }, shell.t('app.name')), el('p', { role: 'status' }, shell.t('app.closing')));
      }
    },
    // S1: once shown, the workspace stays in the page, hidden while another screen covers it; under the failed screen it
    // stays visible, read-only (S11).
    when(seen, () => {
      const covered = (): boolean => asking() || gate() || (screen() !== 'workspace' && screen() !== 'failed');
      return el('div', { class: 'workspace-host', hidden: covered, inert: covered }, view(WorkspaceView, shell));
    }),
  );
}
